import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Footer.css'; // Assuming you have a CSS file for styling

const Footer = () => {
  const { t, i18n } = useTranslation();

  const getCurrentLangPrefix = () => {
    const lang = i18n.language;
    return lang === 'en' ? '' : `/${lang}`;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const productLinks = [
    { name: "Absorex-Pack", path: "/products/absorex-pack" },
    { name: "Absorex-Strips", path: "/products/absorex-strips" },
    { name: "Absorex-V-Box", path: "/products/absorex-v-box" },
    { name: "Absorex-H-Box", path: "/products/absorex-h-box" },
    { name: "Absorex-Blanket", path: "/products/absorex-blanket" },
  ];

  return (
    <footer className="footer">
      {/* Main Footer Content */}
      <div className="footer-main">
        <div className="footer-container">
          <div className="footer-grid">
            {/* Company Info Section */}
            <div className="footer-section">
              <div className="footer-logo">
                <h3 className="logo-text">
                  <span className="logo-deko">DEKO</span>
                  <span className="logo-separator">|</span>
                  <span className="logo-absorex">Absorex™</span>
                </h3>
              </div>
              <p className="footer-description">
                {t('footer.companyInfo.description', 'Professional desiccant solutions and moisture control systems for industrial applications and cargo protection.')}
              </p>
              
              {/* Social Links */}
              <div className="social-links">
                <a href="https://www.linkedin.com/company/absorex/" className="social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="mailto:info@absorex.de" className="social-link" aria-label="Email">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m4 4 16 0 0 16-16 0z"/>
                    <path d="m4 4 8 8 8-8"/>
                  </svg>
                </a>
                <a href="tel:+36706576199" className="social-link" aria-label="Phone">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.quickLinks.title', 'Quick Links')}</h4>
              <ul className="footer-links">
                <li>
                  <Link to={`${getCurrentLangPrefix()}/`} onClick={scrollToTop}>
                    {t('footer.quickLinks.home', 'Home')}
                  </Link>
                </li>
                <li>
                  <Link to={`${getCurrentLangPrefix()}/about`} onClick={scrollToTop}>
                    {t('footer.quickLinks.about', 'About Us')}
                  </Link>
                </li>
                <li>
                  <Link to={`${getCurrentLangPrefix()}/products`} onClick={scrollToTop}>
                    {t('footer.quickLinks.products', 'Products')}
                  </Link>
                </li>
                <li>
                  <Link to={`${getCurrentLangPrefix()}/distributor`} onClick={scrollToTop}>
                    {t('footer.quickLinks.distributor', 'Distributor')}
                  </Link>
                </li>
                <li>
                  <Link to={`${getCurrentLangPrefix()}/contact`} onClick={scrollToTop}>
                    {t('footer.quickLinks.contact', 'Contact')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Products */}
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.products.title', 'Our Products')}</h4>
              <ul className="footer-links">
                {productLinks.map((link) => (
                  <li key={link.name}>
                    <Link to={`${getCurrentLangPrefix()}${link.path}`} onClick={scrollToTop}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.contact.title', 'Contact Information')}</h4>
              <div className="contact-info">
                <div className="contact-item">
                  <div className="contact-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div className="contact-details">
                    <p className="company-name">{t('footer.companyInfo.name')}</p>
                    <p className="address">{t('footer.companyInfo.address')}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div className="contact-details">
                    <p>{t('footer.companyInfo.phone')}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m4 4 16 0 0 16-16 0z"/>
                      <path d="m4 4 8 8 8-8"/>
                    </svg>
                  </div>
                  <div className="contact-details">
                    <a href={`mailto:${t('footer.companyInfo.email').replace('Email: ', '').replace('E-Mail: ', '')}`}>
                      {t('footer.companyInfo.email')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-container">
          <div className="footer-bottom-content">
            <div className="copyright">
              <p>&copy; {new Date().getFullYear()} {t('footer.companyInfo.name')}. {t('footer.copyright', 'All rights reserved.')}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;